import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, Plus, Edit2, Trash2, Search, Loader2, Copy, X, Check, 
  Users, ChevronRight, ChevronDown, Sparkles, Layers, Lock, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Permission module display configuration
const PERMISSION_MODULE_CONFIG = {
  nav: { label: 'Sidebar Navigation', icon: Eye, order: 0, description: 'Controls which menu items appear in sidebar', color: 'blue' },
  dashboard: { label: 'Dashboard', icon: Layers, order: 1, color: 'purple' },
  orders: { label: 'Orders', icon: Layers, order: 2, color: 'green' },
  leads: { label: 'Leads & Sales', icon: Users, order: 3, color: 'yellow' },
  inventory: { label: 'Inventory', icon: Layers, order: 4, color: 'teal' },
  vendors: { label: 'Vendors', icon: Users, order: 5, color: 'orange' },
  purchase_orders: { label: 'Purchase Orders', icon: Layers, order: 6, color: 'pink' },
  deliveries: { label: 'Deliveries', icon: Layers, order: 7, color: 'indigo' },
  marketing: { label: 'Marketing', icon: Sparkles, order: 8, description: 'Inbox, Journeys, WhatsApp, Email, Instagram, Broadcasts', color: 'rose' },
  users: { label: 'User Management', icon: Users, order: 9, color: 'cyan' },
  roles: { label: 'Role Management', icon: Shield, order: 10, color: 'amber' },
  cash: { label: 'Cash Management', icon: Layers, order: 11, color: 'emerald' },
  incentives: { label: 'Incentives', icon: Sparkles, order: 12, color: 'violet' },
  announcements: { label: 'Announcements', icon: Layers, order: 13, color: 'sky' },
  settings: { label: 'Settings', icon: Layers, order: 14, color: 'slate' },
  shopify: { label: 'Shopify Integration', icon: Layers, order: 15, color: 'lime' },
  hr: { label: 'HR & Attendance', icon: Users, order: 16, color: 'fuchsia' }
};

// Pre-built permission templates for quick setup
const PERMISSION_TEMPLATES = {
  'Full Access': {
    description: 'All permissions - full system access',
    icon: Shield,
    color: 'from-amber-500 to-orange-500',
    getPermissions: (allPerms) => Object.keys(allPerms)
  },
  'Sales Team': {
    description: 'Leads, Orders, basic access',
    icon: Users,
    color: 'from-blue-500 to-cyan-500',
    permissions: [
      'nav.dashboard', 'nav.orders', 'nav.leads', 'nav.incentives', 'nav.announcements',
      'dashboard.view', 'orders.view', 'leads.view', 'leads.create', 'leads.edit',
      'incentives.view', 'announcements.view'
    ]
  },
  'Delivery Driver': {
    description: 'Orders, deliveries, cash collection',
    icon: Layers,
    color: 'from-green-500 to-emerald-500',
    permissions: [
      'nav.dashboard', 'nav.orders', 'nav.cash', 'nav.incentives', 'nav.announcements',
      'dashboard.view', 'orders.view', 'orders.update_status',
      'deliveries.view', 'deliveries.accept', 'deliveries.update_status', 'deliveries.upload_pod', 'deliveries.collect_cash',
      'cash.view', 'incentives.view', 'announcements.view'
    ]
  },
  'Operations Manager': {
    description: 'Full operations without admin settings',
    icon: Layers,
    color: 'from-purple-500 to-pink-500',
    permissions: [
      'nav.dashboard', 'nav.orders', 'nav.leads', 'nav.inventory', 'nav.vendors',
      'nav.purchase_orders', 'nav.deliveries', 'nav.cash', 'nav.expenses', 'nav.incentives', 'nav.announcements', 'nav.users',
      'dashboard.view', 'dashboard.analytics',
      'orders.view', 'orders.view_all', 'orders.edit', 'orders.assign_driver', 'orders.update_status', 'orders.print_packing_slip', 'orders.mark_ready_for_dispatch', 'orders.create_invoice', 'orders.override_status', 'orders.manage_returns', 'orders.manage_refunds',
      'abandoned_checkouts.view', 'abandoned_checkouts.trigger_journey',
      'leads.view', 'leads.view_all', 'leads.create', 'leads.edit', 'leads.assign',
      'inventory.view', 'inventory.edit', 'inventory.sync_shopify', 'inventory.settings', 
      'inventory.inwarding', 'inventory.view_history',
      'vendors.view', 'purchase_orders.view',
      'deliveries.view', 'cash.view', 'cash.collect_from_driver',
      'expenses.view', 'expenses.create', 'expenses.approve', 'expenses.settle', 'expenses.cancel', 'expenses.manage_categories',
      'users.view', 'incentives.view', 'incentives.manage', 'announcements.view', 'announcements.create'
    ]
  },
  'Marketing': {
    description: 'Marketing inbox, journeys, WhatsApp, Email, Instagram & broadcasts',
    icon: Sparkles,
    color: 'from-rose-500 to-red-500',
    permissions: [
      'nav.dashboard', 'nav.leads', 'nav.marketing', 'nav.incentives', 'nav.announcements',
      'dashboard.view', 'dashboard.analytics', 'leads.view', 'leads.view_all',
      'marketing.inbox.view', 'marketing.inbox.reply', 'marketing.inbox.assign', 'marketing.inbox.manage', 'marketing.inbox.analytics',
      'marketing.journeys.view', 'marketing.journeys.create', 'marketing.journeys.edit',
      'marketing.whatsapp.view', 'marketing.whatsapp.send',
      'marketing.email.view', 'marketing.email.send',
      'marketing.instagram.view', 'marketing.instagram.send',
      'marketing.broadcasts.view', 'marketing.broadcasts.create', 'marketing.broadcasts.send',
      'abandoned_checkouts.view', 'abandoned_checkouts.manage', 'abandoned_checkouts.sync', 'abandoned_checkouts.trigger_journey',
      'incentives.view', 'announcements.view', 'announcements.create'
    ]
  },
  'Inventory Manager': {
    description: 'Inventory, inwarding, and vendor management',
    icon: Layers,
    color: 'from-teal-500 to-cyan-500',
    permissions: [
      'nav.dashboard', 'nav.inventory', 'nav.vendors', 'nav.purchase_orders', 'nav.announcements',
      'dashboard.view', 
      'inventory.view', 'inventory.edit', 'inventory.sync_shopify', 
      'inventory.inwarding', 'inventory.settings', 'inventory.view_history',
      'vendors.view', 'vendors.create', 'vendors.edit',
      'purchase_orders.view', 'purchase_orders.create', 'purchase_orders.edit',
      'announcements.view'
    ]
  }
};

const RoleManagementPage = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  
  // View mode: 'list' or 'edit'
  const [viewMode, setViewMode] = useState('list');
  const [editingRole, setEditingRole] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  
  // Expanded modules in permission selector
  const [expandedModules, setExpandedModules] = useState({});

  useEffect(() => {
    loadRoles();
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailablePermissions(response.data.permissions || {});
    } catch (error) {
      console.error('Failed to load permissions:', error);
      toast.error('Failed to load permissions');
    } finally {
      setPermissionsLoading(false);
    }
  };

  // Group permissions by module
  const groupedPermissions = useMemo(() => {
    const groups = {};
    Object.entries(availablePermissions).forEach(([key, label]) => {
      const [module] = key.split('.');
      if (!groups[module]) {
        const config = PERMISSION_MODULE_CONFIG[module] || { label: module, icon: Layers, order: 99, color: 'gray' };
        groups[module] = {
          ...config,
          permissions: []
        };
      }
      groups[module].permissions.push({ key, label });
    });
    
    // Sort groups by order
    return Object.entries(groups)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([module, data]) => ({ module, ...data }));
  }, [availablePermissions]);

  const loadRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const customRoles = (response.data.roles || []).filter(r => !r.is_system);
      setRoles(customRoles);
    } catch (error) {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async () => {
    if (!roleName.trim()) {
      toast.error('Please enter a role name');
      return;
    }
    if (selectedPermissions.length === 0) {
      toast.error('Please select at least one permission');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/roles`, {
        name: roleName,
        description: roleDescription,
        permissions: selectedPermissions
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Role created successfully');
      resetAndGoBack();
      loadRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!roleName.trim()) {
      toast.error('Please enter a role name');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/roles/${editingRole.id}`, {
        name: roleName,
        description: roleDescription,
        permissions: selectedPermissions
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Role updated successfully');
      resetAndGoBack();
      loadRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role? Users assigned to this role will lose their permissions.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/roles/${roleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Role deleted successfully');
      loadRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const handleDuplicateRole = (role) => {
    setRoleName(`${role.name} (Copy)`);
    setRoleDescription(role.description || '');
    setSelectedPermissions([...(role.permissions || [])]);
    setEditingRole(null);
    setViewMode('edit');
  };

  const applyTemplate = (templateName) => {
    const template = PERMISSION_TEMPLATES[templateName];
    if (template) {
      const perms = template.getPermissions 
        ? template.getPermissions(availablePermissions)
        : template.permissions;
      setSelectedPermissions(perms);
      toast.success(`Applied "${templateName}" template`);
    }
  };

  const openCreateView = () => {
    setRoleName('');
    setRoleDescription('');
    setSelectedPermissions([]);
    setEditingRole(null);
    setExpandedModules({});
    setViewMode('edit');
  };

  const openEditView = (role) => {
    setEditingRole(role);
    setRoleName(role.name);
    setRoleDescription(role.description || '');
    setSelectedPermissions(role.permissions || []);
    setExpandedModules({});
    setViewMode('edit');
  };

  const resetAndGoBack = () => {
    setRoleName('');
    setRoleDescription('');
    setSelectedPermissions([]);
    setEditingRole(null);
    setViewMode('list');
  };

  const togglePermission = (perm) => {
    setSelectedPermissions(prev => {
      if (prev.includes(perm)) {
        return prev.filter(p => p !== perm);
      }
      return [...prev, perm];
    });
  };

  const toggleModulePermissions = (modulePerms, allChecked) => {
    setSelectedPermissions(prev => {
      if (allChecked) {
        return prev.filter(p => !modulePerms.some(mp => mp.key === p));
      }
      const newPerms = [...prev];
      modulePerms.forEach(mp => {
        if (!newPerms.includes(mp.key)) {
          newPerms.push(mp.key);
        }
      });
      return newPerms;
    });
  };

  const toggleModuleExpand = (module) => {
    setExpandedModules(prev => ({
      ...prev,
      [module]: !prev[module]
    }));
  };

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (role.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-wt-bg">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-wt-gold mx-auto" />
          <p className="mt-4 text-wt-text-secondary">Loading roles...</p>
        </div>
      </div>
    );
  }

  // Edit/Create View
  if (viewMode === 'edit') {
    return (
      <div className="min-h-screen bg-wt-bg">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-wt-surface border-b border-wt-border">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={resetAndGoBack}
                  className="p-2 hover:bg-wt-subtle rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-wt-text-secondary" />
                </button>
                <div>
                  <h1 className="text-2xl font-playfair text-wt-text-primary">
                    {editingRole ? 'Edit Role' : 'Create New Role'}
                  </h1>
                  <p className="text-sm text-wt-text-muted">
                    {editingRole ? `Editing: ${editingRole.name}` : 'Define permissions for your team'}
                  </p>
                </div>
              </div>
              <Button
                onClick={editingRole ? handleUpdateRole : handleCreateRole}
                disabled={saving || !roleName.trim()}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black px-6"
                data-testid="save-role-button"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                {editingRole ? 'Update Role' : 'Create Role'}
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Role Details */}
            <div className="lg:col-span-1 space-y-6">
              {/* Role Info Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-wt-surface border border-wt-border rounded-2xl p-6"
              >
                <h2 className="text-lg font-semibold text-wt-text-primary mb-4 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-wt-gold" />
                  Role Details
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="role-name" className="text-wt-text-primary text-sm font-medium">
                      Role Name *
                    </Label>
                    <Input
                      id="role-name"
                      type="text"
                      value={roleName}
                      onChange={(e) => setRoleName(e.target.value)}
                      placeholder="e.g., Senior Sales Executive"
                      className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary h-12 text-base"
                      data-testid="role-name-input"
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <Label htmlFor="role-description" className="text-wt-text-primary text-sm font-medium">
                      Description
                    </Label>
                    <Textarea
                      id="role-description"
                      value={roleDescription}
                      onChange={(e) => setRoleDescription(e.target.value)}
                      placeholder="Brief description of this role..."
                      className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary min-h-[100px]"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Quick Templates */}
              {!editingRole && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-wt-surface border border-wt-border rounded-2xl p-6"
                >
                  <h2 className="text-lg font-semibold text-wt-text-primary mb-4 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-purple-400" />
                    Quick Templates
                  </h2>
                  <p className="text-xs text-wt-text-muted mb-4">
                    Start with a pre-configured permission set
                  </p>
                  
                  <div className="space-y-2">
                    {Object.entries(PERMISSION_TEMPLATES).map(([name, template]) => {
                      const Icon = template.icon || Shield;
                      return (
                        <button
                          key={name}
                          type="button"
                          onClick={() => applyTemplate(name)}
                          className="w-full p-3 rounded-xl bg-gradient-to-r hover:opacity-90 transition-all text-left group"
                          style={{
                            background: `linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))`
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg bg-gradient-to-br ${template.color}`}>
                              <Icon className="h-4 w-4 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-wt-text-primary truncate">{name}</p>
                              <p className="text-xs text-wt-text-muted truncate">{template.description}</p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-wt-text-muted group-hover:text-wt-gold transition-colors" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Stats Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-wt-gold/10 to-amber-500/5 border border-wt-gold/20 rounded-2xl p-6"
              >
                <div className="text-center">
                  <div className="text-4xl font-bold text-wt-gold">{selectedPermissions.length}</div>
                  <p className="text-sm text-wt-text-secondary mt-1">Permissions Selected</p>
                  <p className="text-xs text-wt-text-muted mt-2">
                    out of {Object.keys(availablePermissions).length} available
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right Column - Permissions */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-wt-surface border border-wt-border rounded-2xl overflow-hidden"
              >
                <div className="p-6 border-b border-wt-border">
                  <h2 className="text-lg font-semibold text-wt-text-primary flex items-center gap-2">
                    <Lock className="h-5 w-5 text-wt-gold" />
                    Permissions
                  </h2>
                  <p className="text-sm text-wt-text-muted mt-1">
                    Select what this role can see and do
                  </p>
                </div>

                {permissionsLoading ? (
                  <div className="flex items-center justify-center p-12">
                    <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
                  </div>
                ) : (
                  <div className="divide-y divide-wt-border max-h-[600px] overflow-y-auto">
                    {groupedPermissions.map(({ module, label, icon: ModuleIcon, description, permissions, color }) => {
                      const allChecked = permissions.every(p => selectedPermissions.includes(p.key));
                      const someChecked = permissions.some(p => selectedPermissions.includes(p.key));
                      const checkedCount = permissions.filter(p => selectedPermissions.includes(p.key)).length;
                      const isExpanded = expandedModules[module] !== false;
                      const Icon = ModuleIcon || Layers;
                      
                      return (
                        <div key={module}>
                          {/* Module Header */}
                          <button
                            type="button"
                            onClick={() => toggleModuleExpand(module)}
                            className="w-full p-4 flex items-center justify-between hover:bg-wt-subtle/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg bg-${color}-500/10`}>
                                <Icon className={`h-4 w-4 text-${color}-400`} />
                              </div>
                              <div className="text-left">
                                <h3 className="text-sm font-medium text-wt-text-primary flex items-center gap-2">
                                  {label}
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    allChecked ? 'bg-wt-gold/20 text-wt-gold' : 
                                    someChecked ? 'bg-wt-subtle text-wt-text-secondary' : 
                                    'bg-wt-subtle text-wt-text-muted'
                                  }`}>
                                    {checkedCount}/{permissions.length}
                                  </span>
                                </h3>
                                {description && (
                                  <p className="text-xs text-wt-text-muted">{description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleModulePermissions(permissions, allChecked);
                                }}
                                className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${
                                  allChecked 
                                    ? 'bg-wt-gold text-black hover:bg-wt-gold-hover' 
                                    : 'bg-wt-subtle text-wt-text-secondary hover:bg-wt-gold/10 hover:text-wt-gold'
                                }`}
                              >
                                {allChecked ? 'Deselect All' : 'Select All'}
                              </button>
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-wt-text-muted" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-wt-text-muted" />
                              )}
                            </div>
                          </button>

                          {/* Permissions Grid */}
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {permissions.map(({ key, label: permLabel }) => {
                                    const isChecked = selectedPermissions.includes(key);
                                    const actionLabel = key.split('.').slice(1).join(' ').replace(/_/g, ' ');
                                    return (
                                      <label
                                        key={key}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-all ${
                                          isChecked 
                                            ? 'bg-wt-gold/10 border-2 border-wt-gold' 
                                            : 'bg-wt-bg border-2 border-transparent hover:border-wt-border'
                                        }`}
                                      >
                                        <Checkbox
                                          checked={isChecked}
                                          onCheckedChange={() => togglePermission(key)}
                                          className="border-wt-border data-[state=checked]:bg-wt-gold data-[state=checked]:border-wt-gold"
                                        />
                                        <div className="flex-1 min-w-0">
                                          <span className={`text-sm font-medium capitalize ${isChecked ? 'text-wt-gold' : 'text-wt-text-primary'}`}>
                                            {actionLabel}
                                          </span>
                                          <p className="text-xs text-wt-text-muted truncate">{permLabel}</p>
                                        </div>
                                      </label>
                                    );
                                  })}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="min-h-screen bg-wt-bg">
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-wt-surface via-wt-surface to-wt-gold/5 border-b border-wt-border">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 rounded-xl bg-wt-gold/10">
                  <Shield className="h-8 w-8 text-wt-gold" />
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-playfair text-wt-text-primary">
                Role Management
              </h1>
              <p className="text-lg text-wt-text-secondary mt-2">
                Create and manage custom roles with granular permissions
              </p>
            </div>
            <Button 
              onClick={openCreateView}
              className="bg-wt-gold hover:bg-wt-gold-hover text-black h-12 px-6 text-base"
              data-testid="create-role-button"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create New Role
            </Button>
          </div>

          {/* Search */}
          <div className="mt-8 relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-wt-text-muted" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search roles by name or description..."
              className="pl-12 h-12 bg-wt-bg border-wt-border text-wt-text-primary text-base rounded-xl"
              data-testid="search-roles-input"
            />
          </div>
        </div>
      </div>

      {/* Roles Grid */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {filteredRoles.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="p-6 rounded-full bg-wt-subtle inline-block mb-4">
              <Shield className="h-12 w-12 text-wt-text-muted" />
            </div>
            <h3 className="text-xl text-wt-text-primary mb-2">No Custom Roles Yet</h3>
            <p className="text-wt-text-secondary mb-6">
              {searchTerm ? 'No roles match your search' : 'Create your first custom role to get started'}
            </p>
            {!searchTerm && (
              <Button 
                onClick={openCreateView}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Role
              </Button>
            )}
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRoles.map((role, index) => (
              <motion.div
                key={role.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-wt-surface border border-wt-border rounded-2xl overflow-hidden hover:border-wt-gold/50 transition-all group"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-xl bg-wt-gold/10 group-hover:bg-wt-gold/20 transition-colors">
                      <Shield className="h-6 w-6 text-wt-gold" />
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDuplicateRole(role)}
                        className="p-2 hover:bg-wt-subtle rounded-lg transition-colors text-wt-text-muted hover:text-wt-text-primary"
                        title="Duplicate"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openEditView(role)}
                        className="p-2 hover:bg-wt-subtle rounded-lg transition-colors text-wt-text-muted hover:text-wt-text-primary"
                        title="Edit"
                        data-testid={`edit-role-${role.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteRole(role.id)}
                        className="p-2 hover:bg-red-500/10 rounded-lg transition-colors text-wt-text-muted hover:text-red-400"
                        title="Delete"
                        data-testid={`delete-role-${role.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <h3 className="text-xl font-semibold text-wt-text-primary mb-2">
                    {role.name}
                  </h3>
                  <p className="text-sm text-wt-text-secondary mb-4 line-clamp-2">
                    {role.description || 'No description provided'}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-wt-border">
                    <div className="flex items-center gap-2">
                      <Lock className="h-4 w-4 text-wt-text-muted" />
                      <span className="text-sm text-wt-text-muted">
                        {role.permissions?.length || 0} permissions
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditView(role)}
                      className="text-wt-gold hover:text-wt-gold-hover hover:bg-wt-gold/10"
                    >
                      Edit
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleManagementPage;
